import Decimal from "decimal.js";
import { ArticleRepository, HttpArticleRepository, InmemoryRepository } from "repositories/article/ArticleRepository";
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ArticleQuery, ArticleResponse, CalculateListPriceRequest, CalculateListPriceResponse } from "types/Article";
import { v4 as uuidv4 } from 'uuid';
import reportWebVitals from "reportWebVitals";
import { PageView } from "types/Diagnostics";
import { ArticleBatchListPriceAPI, ArticleListPriceAPI, HttpArticleListPriceAPI } from "repositories/article/ArticleAPI";
import { CustomerRepository, HttpCustomerRepository, InmemoryCustomerRepository } from "repositories/customer/CustomerRepository";
import { Account, AccountDetails, AccountDetailsQuery, AccountQuery } from "types/Customer";
import { Fetcher } from "hooks/fetch/Fetch";
import { HttpProductFamilyRepository, InmemoryProductFamilyRepository, ProductFamilyRepository } from "repositories/productFamily/ProductFamilyRepository";

enum Environment {
  local,
  dev,
  test,
  prod
}

export class DIContainer {

  static environment: Environment | null = null;
  static appInsights: ApplicationInsights | null = null;
  static listPriceAPI: ArticleListPriceAPI | null = null;

  private static getEnvironment(): Environment {
    if (this.environment == null) {
      throw new Error('environment not set');
    }

    return this.environment;
  }

  private static isEnvironment(env: Environment): boolean {
    return this.getEnvironment() === env;
  }

  private static isAnyEnvironment(...envs: Environment[]): boolean {
    for (let env of envs) {
      if (this.getEnvironment() === env) {
        return true;
      }
    }
    return false;
  }

  private static useMockData(): boolean {
    let useMockData = process.env.REACT_APP_USE_MOCK_DATA;
    return useMockData !== undefined && useMockData.toLowerCase().startsWith("true");
  }

  private static getMockAccounts(): Account[] {
    return [
      { number: '100001', name: 'Acme Manufacturing AB', salesCompanyId: 'DK2', siteId: "DK2-001", isBlocked: false },
      { number: '100145', name: 'Nordic Process Ventilation', salesCompanyId: 'DK2', siteId: "DK2-002", isBlocked: false },
      { number: '100278', name: 'Blue Harbor Fabrication', salesCompanyId: 'DK2', siteId: "DK2-003", isBlocked: false },
      { number: '200010', name: 'Demo Customer Sweden', salesCompanyId: 'SE1', siteId: "SE1-001", isBlocked: false },
    ];
  }

  private static getMockAccountDetails(): Map<string, AccountDetails> {
    return new Map<string, AccountDetails>([
      ['DK2:100001', { discountGroup: 'A10', priceGroup: 'PARTNER' }],
      ['DK2:100145', { discountGroup: 'B15', priceGroup: 'SERVICE' }],
      ['DK2:100278', { discountGroup: 'C05', priceGroup: 'OEM' }],
      ['SE1:200010', { discountGroup: 'A10', priceGroup: 'PARTNER' }],
    ]);
  }

  private static setEnvironment() {
    let env = process.env.REACT_APP_ENVIRONMENT;
    if (env === "" || env === undefined) {
      throw new Error('environment not set');
    }

    switch (env) {
      case "local":
        this.environment = Environment.local;
        break;
      case "dev":
        this.environment = Environment.dev;
        break;
      case "test":
        this.environment = Environment.test;
        break;
      case "prod":
        this.environment = Environment.prod;
        break;
      default:
        throw new Error('unrecognized environment: ' + env);
    }

  }

  static init() {
    this.setEnvironment();
    let appInsightsConnectionString = process.env.REACT_APP_APP_INSIGHTS_CONNECTION_STRING;
    if (appInsightsConnectionString !== undefined) {
      this.appInsights = new ApplicationInsights({
        config: {
          connectionString: appInsightsConnectionString,
        }
      })
      this.appInsights.loadAppInsights();
    }

    if (DIContainer.appInsights !== null) {
      reportWebVitals((metric) => {
        const { name, delta, value, id } = metric;
        DIContainer.appInsights!.trackEvent({
          name: `web-vital-${name}`,
          properties: {
            name,
            delta: delta.toString(),
            value: value.toString(),
            id,
            entries: metric.entries,
          }
        });
      });
    } else {
      reportWebVitals(console.log);
    }
  }

  static getPageViewTracker(): (view: PageView) => void {
    return (view: PageView) => {
      if (this.appInsights != null) {
        this.appInsights.trackPageView(view);
      }
    };
  }

  static getArticleBatchAPI(fetcher: Fetcher): ArticleBatchListPriceAPI {
    let api: ArticleBatchListPriceAPI | null = null;
    if (this.isEnvironment(Environment.local)) {
      //api = new InmemoryArticleAPI();
    } else if (this.isAnyEnvironment(Environment.dev, Environment.test, Environment.prod)) {
      api = new HttpArticleListPriceAPI(
        process.env.REACT_APP_ARTICLE_LIST_PRICE_BASE_URL as string,
        fetcher,
      )
    }

    if (api != null) {
      //api = new CachedArticleListPriceAPI(api);
      if (this.appInsights != null) {
        //api = this.ArticleAPIWithTelemetry(api, this.appInsights)
      }

      return api;
    }
    throw new Error("missing ArticleAPI implementation for environment " + process.env.REACT_APP_ENVIRONMENT);
  }

  static getCustomerRepository(fetcher: Fetcher): CustomerRepository {
    let repo: CustomerRepository | null = null;
    if (this.isEnvironment(Environment.local) || this.useMockData()) {
      repo = new InmemoryCustomerRepository(this.getMockAccounts(), this.getMockAccountDetails());
    } else if (this.isAnyEnvironment(Environment.dev, Environment.test, Environment.prod)) {
      repo = new HttpCustomerRepository(
        process.env.REACT_APP_CUSTOMER_SEARCH_BASE_URL as string,
        process.env.REACT_APP_CUSTOMER_DETAILS_BASE_URL as string,
        fetcher,
      );
    }

    if (repo != null) {
      if (this.appInsights != null) {
        repo = this.CustomerRepositoryWithTelemetry(repo, this.appInsights)
      }

      return repo;
    }
    throw new Error("missing ArticleRepository implementation for environment " + process.env.REACT_APP_ENVIRONMENT);
  }

  static getProductFamilyRepository(fetcher: Fetcher): ProductFamilyRepository {
    if (this.isEnvironment(Environment.local) || this.useMockData() || process.env.REACT_APP_PRODUCT_FAMILIES_BASE_URL === 'mocked') {
      return new InmemoryProductFamilyRepository([
        { id: 'extraction-arms', name: 'Extraction arms', area: 'Extraction' },
        { id: 'extraction-hoods', name: 'Extraction hoods', area: 'Extraction' },
        { id: 'extraction-booms', name: 'Extraction booms', area: 'Extraction' },
        { id: 'fans', name: 'Fans', area: 'Fans' },
        { id: 'fan-controls', name: 'Fan controls', area: 'Fans' },
        { id: 'filter-units', name: 'Filter units', area: 'Filtration' },
        { id: 'filter-cartridges', name: 'Filter cartridges', area: 'Filtration' },
        { id: 'ducting', name: 'Ducting', area: 'Ducting' },
        { id: 'ducting-fittings', name: 'Ducting fittings', area: 'Ducting' },
        { id: 'nordfab-ducting', name: 'Nordfab ducting', area: 'Ducting' },
        { id: 'nordfab-fittings', name: 'Nordfab fittings', area: 'Ducting' },
      ]);
    }

    return new HttpProductFamilyRepository(
      process.env.REACT_APP_PRODUCT_FAMILIES_BASE_URL ?? '', 
      process.env.REACT_APP_CUSTOMER_PRODUCT_AREA_FAMILIES_BASE_URL ?? '',
      fetcher,
    );
  }

  static getArticleRepository(fetcher: Fetcher): ArticleRepository {
    let repo: ArticleRepository | null = null;
    if (this.isEnvironment(Environment.local)) {
      repo = new InmemoryRepository([
        {
          id: "10230040",
          category: "Extr. Arms, kit and Welding tables",
          description: "Kit fume extractor 3m orig. 400V",
          price: new Decimal(15487),
          costPrice: new Decimal(5745.71),
          currency: "USD",
          assetClass: "A",
        },
        {
          id: "10230050",
          category: "Extr. Arms, kit and Welding tables",
          description: "Kit fume extractor 4mv orig. 400V",
          price: new Decimal(16309),
          costPrice: new Decimal(6050.44),
          currency: "USD",
          assetClass: "B",
        },
        {
          id: "10311238",
          category: "Common accessories Capture & Extraction units",
          description: "Spot light fume extractor orig.",
          price: new Decimal(1818),
          costPrice: new Decimal(632.81),
          currency: "USD",
          assetClass: "C",
        },
      ]);
    } else if (this.isAnyEnvironment(Environment.dev, Environment.test, Environment.prod)) {
      repo = new HttpArticleRepository(
        process.env.REACT_APP_ARTICLE_SEARCH_BASE_URL as string,
        fetcher,
      );
    }

    if (repo != null) {
      if (this.appInsights != null) {
        repo = this.ArticleRepositoryWithTelemetry(repo, this.appInsights)
      }

      return repo;
    }
    throw new Error("missing ArticleRepository implementation for environment " + this.getEnvironment());
  }


  static ArticleRepositoryWithTelemetry(repository: ArticleRepository, appInsights: ApplicationInsights): ArticleRepository {
    return {
      async findBy(query: ArticleQuery): Promise<ArticleResponse> {
        return await DIContainer.WithTelemetry((query: ArticleQuery) => repository.findBy(query), appInsights, 'ArticleRepository.findBy')(query);
      }
    }
  }

  static ArticleAPIWithTelemetry(api: ArticleListPriceAPI, appInsights: ApplicationInsights): ArticleListPriceAPI {
    return {
      async calculateListPrice(request: CalculateListPriceRequest): Promise<CalculateListPriceResponse> {
        return DIContainer.WithTelemetry((request: CalculateListPriceRequest) => api.calculateListPrice(request), appInsights, 'ArticleAPI.calculateListPrice')(request);
      }
    }
  }

  static CustomerRepositoryWithTelemetry(repository: CustomerRepository, appInsights: ApplicationInsights): CustomerRepository {
    return {
      async findBy(query: AccountQuery): Promise<Account[]> {
        return await DIContainer.WithTelemetry((query: AccountQuery) => repository.findBy(query), appInsights, 'ArticleRepository.findBy')(query);
      },

      async findDetailsBy(query: AccountDetailsQuery): Promise<AccountDetails | undefined> {
        return await DIContainer.WithTelemetry((query: AccountDetailsQuery) => repository.findDetailsBy(query), appInsights, 'ArticleRepository.findDetailsBy')(query);
      }
    }
  }

  static WithTelemetry<T extends (...args: any[]) => Promise<any>>(
    func: T,
    appInsights: ApplicationInsights,
    functionName: string
  ): T {
    return (async (...args: any[]) => {
      let id = uuidv4();
      const start = performance.now();
      try {
        const result = await func(...args);
        const end = performance.now();

        appInsights.trackDependencyData({
          id: id,
          name: `${functionName}`,
          duration: Math.round(end - start),
          success: true,
          responseCode: 200,
          data: JSON.stringify(args),
          type: 'API',
        });

        return result;
      } catch (error) {
        const end = performance.now();
        appInsights.trackException({ exception: error as Error });
        appInsights.trackDependencyData({
          id: id,
          name: `${functionName}`,
          duration: Math.round(end - start),
          success: false,
          responseCode: 500,
          data: JSON.stringify(args),
          type: 'API',
        });

        throw error;
      }
    }) as T;
  }

  static LoginURL(): string {
    let url = process.env.REACT_APP_LOGIN_URL;
    if (url === "" || url === undefined) {
      throw new Error('Login url not set');
    }

    return url;
  }

  static RefreshURL(): string {
    let url = process.env.REACT_APP_REFRESH_TOKEN_URL;
    if (url === "" || url === undefined) {
      throw new Error('Refresh token url not set');
    }

    return url;
  }
}
